import { Inertia } from '@inertiajs/inertia'
import { FunctionalComponent, h } from '@stencil/core'
import { Fragment, JSXBase } from '@stencil/core/internal'
import { createStore } from '@stencil/store'
import Axios from 'axios'
import axios from 'axios'
import pluralize from 'pluralize'
import { dayjs } from 'src/lib/dayjs'
import { ColorsType } from 'src/tailwind'
import { InlineIconText } from 'src/ui/components'
import { logWithBadge } from 'src/utils/logging'

import { AppSharedPropsStore, IBanner, IGamePoints } from '../app'
import { InertiaLink } from '../inertia/link/link'
import { classes, colorClasses } from '../utils/css'
import { useStoredValue } from '../utils/storage'
import { ActionName, ActionState } from './types'

declare global {
  interface LocalStorageKeys {
    gamificationMuted: boolean
  }
}

export const GameStore = createStore({
  isEnabled: true,
  showDialog: false,
  totalPoints: 0,
  newPoints: 0,
  monthPoints: 0,
  badgePoints: 0,
  tooltipPoints: 0,
  showTooltip: false,
  isMayor: false,
  actions: {} as Partial<Record<ActionName, ActionState>>,
  dismiss_url: '',
  lastDismissedBannerId: 0,
  points_url: '',
  isMuted: false,
})

const { state } = GameStore

let tooltipPointsTimeout: number
let dialogTimeout: number

function showDialog() {
  window.clearTimeout(dialogTimeout)
  state.showDialog = true
}

function hideDialog() {
  window.clearTimeout(dialogTimeout)
  dialogTimeout = window.setTimeout(() => {
    state.showDialog = false
  }, 100)
}

GameStore.use(useStoredValue(GameStore, 'gamificationMuted', 'isMuted', false))

GameStore.onChange('tooltipPoints', () => {
  window.clearTimeout(tooltipPointsTimeout)
  tooltipPointsTimeout = window.setTimeout(() => (state.showTooltip = false), 3000)
})

export function showPoints() {
  state.badgePoints = state.totalPoints
}

export async function dismissBanner(banner: IBanner) {
  if (AppSharedPropsStore.state.adminConfig?.isImpersonating) {
    return
  }
  state.lastDismissedBannerId = banner.id
  const dismissUrl = banner.metadata?.dismissUrl || state.dismiss_url
  if (dismissUrl) {
    await Axios.post(dismissUrl, { banner_id: banner.id })
  }
}

export function setGameState(enabled: boolean, isMayor: boolean, actions: Partial<Record<ActionName, ActionState>>) {
  state.isEnabled = enabled
  state.isMayor = isMayor
  state.actions = actions
}

export async function fetchPoints(withBanner: boolean) {
  if (state.points_url && !AppSharedPropsStore.state.inElectron && !AppSharedPropsStore.state.inZapp) {
    await Axios.get<IGamePoints>(state.points_url, { params: { with_banner: withBanner } }).then(async (response) => {
      // Must update showTooltip before newPoints
      if (!state.showTooltip) {
        state.showTooltip = response.data.added > 0
        if (state.showTooltip) {
          state.tooltipPoints = response.data.added
        }
      }
      state.totalPoints = response.data.total
      state.monthPoints = response.data.month
      state.newPoints = response.data.added
      if (response.data.added_on_page) {
        showPoints()
      } else {
        state.badgePoints = state.totalPoints - state.newPoints
      }
    })
  }
}

export const AnnouncementBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => (
  <div class='flex flex-row justify-center items-center p-1 text-center text-black bg-info-warn rounded-t-m'>
    <div class='flex justify-center items-center mx-auto'>
      <div class='flex flex-col leading-xl'>
        <span class='font-bold'>{banner.message[0]}</span>
        {banner.message.length > 1 && <span>{banner.message.slice(1).join(' ')}</span>}
      </div>
      {banner.learnMoreUrl && (
        <div class='flex flex-col'>
          <a class='py-1 px-4 ml-4 text-sm font-bold uppercase rounded-md border-2' href={banner.learnMoreUrl}>
            Learn More
          </a>
        </div>
      )}
    </div>
    <button
      class='mt-1 text-4xl text-black border-none opacity-30'
      onClick={() => {
        dismissBanner(banner)
      }}
    >
      <ui-icon icon='x-circle-outline'></ui-icon>
    </button>
  </div>
)

export const CallAccessRequestBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  if (banner.metadata.response === 'granted') {
    return (
      <div class='flex flex-row justify-center items-center p-1 h-11 text-center text-black bg-brand rounded-t-m'>
        {banner.metadata.actions.viewSharingSettingsUrl ? (
          <span class='mx-auto'>
            ✅ <span class='font-extrabold'>{banner.metadata.requestedBy.nameAndEmail}</span> has been granted access.
            Want to automatically share recordings or summaries with attendees?{' '}
            <a class='italic font-extrabold underline' href={banner.metadata.actions.viewSharingSettingsUrl}>
              View Sharing Settings
            </a>
          </span>
        ) : (
          <span class='mx-auto'>
            ✅ <span class='font-extrabold'>{banner.metadata.requestedBy.nameAndEmail}</span> has been granted access to
            view this recording
          </span>
        )}
      </div>
    )
  } else {
    return (
      <div class='flex flex-row justify-center items-center p-1 h-11 text-center text-black bg-info-warn rounded-t-m'>
        <span class='mx-auto'>
          🔒 <span class='font-extrabold'>{banner.metadata.requestedBy.nameAndEmail}</span> would like to view this
          recording
          <button
            class='py-1 pr-2 pl-1 ml-2 text-sm font-semibold rounded border-2 border-black'
            onClick={async () => {
              await axios.put(banner.metadata.actions.crudUrl, { response: 'granted' }).finally(() => Inertia.reload())
            }}
          >
            <ui-icon icon='check' class='align-middle' size='xl' />
            Approve
          </button>
        </span>

        <button
          class='mt-1 text-4xl text-black border-none opacity-30'
          onClick={async () => {
            dismissBanner(banner)
            await axios.put(banner.metadata.actions.crudUrl, { response: 'dismissed' }).finally(() => Inertia.reload())
          }}
        >
          <ui-icon icon='x-circle-outline'></ui-icon>
        </button>
      </div>
    )
  }
}

export const FreePreviewBanner: FunctionalComponent<{
  banner: IBanner
  message: string
}> = ({ banner, message }) => {
  return (
    <div class='flex flex-row justify-center items-center p-7px text-base font-medium text-center text-black bg-accent-3'>
      <span class='mx-auto'>
        🔥 <span class='font-extrabold uppercase'>Free Preview</span> &ndash; {message}{' '}
        <a class='font-extrabold underline whitespace-nowrap' href={banner.metadata.learnMoreUrl} target='_blank'>
          Learn More
        </a>
      </span>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const PremiumPlanUserFreePreviewBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  const endsAtDate = dayjs(banner.metadata.freePreviewEndsAt)
  const dateFormat = endsAtDate.isSame(dayjs(), 'year') ? 'MMM D' : 'MMM D, YYYY'
  const until = endsAtDate.format(dateFormat)
  return (
    <div class='flex flex-row justify-center items-center p-7px text-base font-medium text-center text-black bg-accent-3'>
      <span class='mx-auto'>
        🎁 <span class='font-bold uppercase'>Premium Free Preview:</span> Advanced AI Summaries, AI Action Items, Ask
        Fathom, Zapier, and more for free until {until}.{' '}
        <a class='font-extrabold underline whitespace-nowrap' href={banner.metadata.learnMoreUrl} target='_blank'>
          Learn More
        </a>
      </span>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const ShareSignUpPromptBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  console.log('banner', banner)
  return (
    <div class='flex flex-row justify-center items-center p-7px text-base font-medium text-center text-black bg-info-warn'>
      <span class='mx-auto'>
        <a class='font-extrabold underline whitespace-nowrap' href={banner.metadata.signupUrl} target='_blank'>
          Sign up
        </a>{' '}
        for your free Fathom account to access meeting highlights and comments.
      </span>
    </div>
  )
}

export const FreePreviewWarningBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }, children) => {
  return (
    <div class='flex flex-row justify-center items-center py-7px px-15px text-large font-medium leading-18px text-center text-black bg-info-warn'>
      <span class='mx-auto'>{children}</span>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const PremiumPlanUserFreePreviewEndingBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <FreePreviewWarningBanner banner={banner}>
      🚨 <span class='font-extrabold uppercase'>{banner.metadata.freePreviewTimeRemaining} Left</span> - Fathom Premium
      free preview ends soon!{' '}
      <a class='font-extrabold underline whitespace-nowrap' href={banner.metadata.upgradeUrl} target='_blank'>
        Upgrade now
      </a>{' '}
      and save {banner.metadata.percentOff}% on keeping your Premium perks.{' '}
      <a class='font-extrabold underline whitespace-nowrap' href={banner.metadata.learnMoreUrl} target='_blank'>
        Learn More
      </a>
    </FreePreviewWarningBanner>
  )
}

export const PremiumPlanUserFreePreviewEndedBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <FreePreviewWarningBanner banner={banner}>
      ⌛ <span class='font-extrabold uppercase'>Premium Preview Ended</span> -{' '}
      <a class='font-extrabold underline whitespace-nowrap' href={banner.metadata.upgradeUrl} target='_blank'>
        Upgrade now
      </a>{' '}
      to restore access to Premium features like Advanced AI Summaries, AI Action Items, and Ask Fathom.{' '}
      <a class='underline whitespace-nowrap' href={banner.metadata.learnMoreUrl} target='_blank'>
        Learn More
      </a>
    </FreePreviewWarningBanner>
  )
}

export const AdvancedAiTeamFreePreviewBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <FreePreviewBanner
      banner={banner}
      message='Enjoy AI summary templates, AI-generated action items, and follow-up emails for free until Jan 15.'
    />
  )
}

export const PremiumPlanUserAiFreePreviewSummer2024Banner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <div class='flex flex-row justify-center items-center p-7px text-base font-medium text-center text-black bg-accent-3'>
      <span class='mx-auto'>
        🎁 <span class='font-bold uppercase'>Free Premium October!</span> Enjoy Ask Fathom, AI Action Items, and
        Advanced AI Meeting Summaries for free all month long.{' '}
        <a class='font-extrabold underline whitespace-nowrap' href={banner.metadata.learnMoreUrl} target='_blank'>
          Learn More
        </a>
      </span>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const CompleteOnboardingBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <div class='flex flex-row justify-center items-center p-1 text-base text-center text-black bg-info-warn spacing-x-3 rounded-t-m'>
      <span>
        🚀 <span class='font-extrabold'>Complete your Fathom Setup (1-2 mins)</span> to unlock additional features and
        unlimited usage
      </span>

      <a class='py-1 px-4 text-sm font-semibold rounded border-2 border-black' href={banner.clickHereUrl}>
        Finish Setup
      </a>
    </div>
  )
}

export const ClickHereBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  // Because the banner renders multiple times on page load,
  // clamping Math.random() to [0, 1] can cause the message to
  // visibly change back and forth during page load
  const message =
    banner.message.length > 1 ? banner.message[dayjs().minute() % banner.message.length] : banner.message[0]
  const regex = /(.*)(click here.*)$/i
  const result = regex.exec(message)
  return (
    <div class='flex flex-row justify-center items-center p-1 text-center text-black bg-info-warn rounded-t-m'>
      <div class='flex justify-center items-center mx-auto'>
        <div class='flex flex-col leading-xl'>
          <span class='font-bold'>
            {result[1]}{' '}
            <a class='underline' target='_blank' href={banner.clickHereUrl}>
              {result[2]}
            </a>
            {result[3] && <Fragment> {result[3]}</Fragment>}
          </span>
        </div>
      </div>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const ScheduleCallBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  // Because the banner renders multiple times on page load,
  // clamping Math.random() to [0, 1] can cause the message to
  // visibly change back and forth during page load
  const message =
    banner.message.length > 1 ? banner.message[dayjs().minute() % banner.message.length] : banner.message[0]
  const regex = /(.*)(schedule\s(?:a\s)?call)(.*)/i
  const result = regex.exec(message)
  return (
    <div class='flex flex-row justify-center items-center p-1 text-center text-black bg-info-warn rounded-t-m'>
      <div class='flex justify-center items-center mx-auto'>
        <div class='flex flex-col leading-xl'>
          <span class='font-bold'>
            {result[1]}{' '}
            <a class='underline' target='_blank' href={banner.scheduleCallUrl}>
              {result[2]}
            </a>
            {result[3] && <Fragment> {result[3]}</Fragment>}
          </span>
        </div>
      </div>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const ProductHuntLaunchBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <div
      class='flex flex-row justify-center items-center p-1 text-center text-white rounded-t-m'
      style={{ background: '#ca5f3a' }}
    >
      <div class='flex flex-col lg:flex-row justify-center items-center mx-auto spacing-x-2'>
        <a
          href='https://www.producthunt.com/posts/fathom-2-0-2?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-fathom&#0045;2&#0045;0&#0045;2'
          target='_blank'
        >
          <img
            src='https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=380138&theme=light'
            alt='Fathom&#0032;2&#0046;0 - Free&#0032;AI&#0032;Notetaker&#0032;&#0045;&#0032;never&#0032;take&#0032;notes&#0032;on&#0032;a&#0032;Zoom&#0032;call&#0032;again&#0033; | Product Hunt'
            style={{ width: '250px', height: '54px' }}
            width='250'
            height='54'
          />
        </a>
        <div class='flex flex-col items-start text-lg'>
          <span class='font-extrabold'>
            Fathom is on Product Hunt today! Would love your support <ui-icon icon='heart-fill' class='text-sm' />
          </span>
          <span class='text-md italic'>
            If you're loving Fathom, please help us secure Product of the Day by{' '}
            <a
              class='underline'
              href='https://www.producthunt.com/posts/fathom-2-0-2?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-fathom&#0045;2&#0045;0&#0045;2'
              target='_blank'
            >
              writing a short review
            </a>
          </span>
        </div>
      </div>
      <button
        class='text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const TeamEditionTrialBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  const headlineText =
    banner.metadata.daysRemaining > 0
      ? `${pluralize('day', banner.metadata.daysRemaining, true)} remaining in your Team Edition trial!`
      : `Your Team Edition trial ends in ${
          banner.metadata.hoursRemaining >= 1 ? pluralize('hour', banner.metadata.hoursRemaining, true) : '< 1 hour'
        }!`
  return (
    <div class='flex items-center h-10 text-center text-black bg-accent-3 rounded-t-m'>
      <div class='flex items-center mx-auto text-xlarge text-left'>
        <span class='pr-5px'>🔥</span>
        <span class='pr-5px font-extrabold'>{headlineText}</span>
        <a class='font-medium underline' target='_blank' href={banner.metadata.checkoutUrl}>
          Enter payment info to lock in pricing
        </a>
      </div>
    </div>
  )
}

export const PaymentFailureBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <div class='flex flex-row justify-center items-center p-1 text-center text-black bg-info-warn rounded-t-m'>
      <div class='flex justify-center items-center mx-auto'>
        <span class='pr-5px font-extrabold'>
          <ui-icon icon='exclamation-octagon' size='2xl' class='mr-1 align-middle' />
          Payment Failure -
        </span>
        <span class='pr-10px'>Update your credit card to maintain access to your Team Edition</span>
        <a
          class='font-medium border-2 border-black'
          target='_blank'
          style={{ padding: '.3em .6em', borderRadius: '.4em' }}
          href={banner.metadata.paymentSubscriptionManageUrl}
        >
          Update credit card
        </a>
      </div>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const WefunderExclusiveBanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  //const minimumPoints = banner.messageType === 'wefunder_exclusive' ? 100 : 25
  return (
    <div class='flex flex-row items-center p-1 h-14 text-center text-black bg-info-warn rounded-t-m'>
      <div class='flex items-center mx-auto'>
        <div class='pr-13px' style={{ fontSize: '32px', lineHeight: '39px' }}>
          🚀
        </div>
        <div class='flex flex-col text-left'>
          <span class='text-2xlarge font-extrabold'>Last chance to become an investor in Fathom!</span>
          <span class='text-md italic font-medium'>
            We expect to be fully subscribed in the next week!{' '}
            <a class='underline' target='_blank' href='https://wefunder.com/fathom.video/'>
              Full pitch and investment details (min $100)
            </a>
          </span>
        </div>
      </div>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline'></ui-icon>
      </button>
    </div>
  )
}

export const WefunderSeriesABanner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  return (
    <div class='flex justify-between items-center py-5px px-17px h-14 text-left text-black bg-accent-3 rounded-t-m'>
      <div class='flex items-center'>
        <div class='pr-13px' style={{ fontSize: '32px', lineHeight: '32px' }}>
          🚀
        </div>
        <div class='flex flex-col text-left'>
          <span class='text-2xlarge font-extrabold'>Invest in Fathom's Series A Fundraising!</span>
          <span class='text-md font-medium'>
            We've reserved 10% of our $17M round for you, our amazing users! Act fast, over $700K invested by Fathom
            users in first 48 hrs. $100 minimum
            <a
              class='pl-7px font-extrabold underline uppercase'
              target='_blank'
              href='https://wefunder.com/fathom.video/'
            >
              Learn More
            </a>
          </span>
        </div>
      </div>
      <button
        class='mt-1 text-4xl text-black border-none opacity-30'
        onClick={() => {
          dismissBanner(banner)
        }}
      >
        <ui-icon icon='x-circle-outline' />
      </button>
    </div>
  )
}

export const Banner: FunctionalComponent<{
  banner: IBanner
}> = ({ banner }) => {
  logWithBadge('Banner', banner)
  // Skip if this banner was dismissed
  if (
    (['announcement', 'click_here', 'schedule_call', 'override'].includes(banner.type) ||
      banner.messageType === 'payment_past_due') &&
    banner.id === state.lastDismissedBannerId
  ) {
    return
  }

  // Skip if this banner is ineligible for display
  if (banner.isGamificationOnly && !GameStore.state.isEnabled) {
    return
  }

  if (banner.type === 'override' && banner.messageType === 'call_access_request') {
    return <CallAccessRequestBanner banner={banner} />
  } else if (banner.type === 'override' && banner.messageType === 'premium_plan_user_free_preview') {
    return <PremiumPlanUserFreePreviewBanner banner={banner} />
  } else if (banner.type === 'override' && banner.messageType === 'premium_plan_user_free_preview_ending') {
    return <PremiumPlanUserFreePreviewEndingBanner banner={banner} />
  } else if (banner.type === 'override' && banner.messageType === 'premium_plan_user_free_preview_ended') {
    return <PremiumPlanUserFreePreviewEndedBanner banner={banner} />
  } else if (banner.type === 'override' && banner.messageType === 'premium_plan_ai_free_preview_summer_2024') {
    return <PremiumPlanUserAiFreePreviewSummer2024Banner banner={banner} />
  } else if (banner.type === 'override' && banner.messageType === 'advanced_ai_team_free_preview') {
    return <AdvancedAiTeamFreePreviewBanner banner={banner} />
  } else if (banner.type === 'override' && banner.messageType === 'share_sign_up_prompt') {
    return <ShareSignUpPromptBanner banner={banner} />
  } else if (banner.type === 'click_here' && banner.messageType === 'complete_onboarding') {
    return <CompleteOnboardingBanner banner={banner} />
  } else if (banner.type === 'prompt' && banner.messageType === 'completed_onboarding') {
    // Shown from the layout as a dialog box now
  } else if (banner.type === 'prompt' && banner.messageType === 'team_edition_trial') {
    return <TeamEditionTrialBanner banner={banner} />
  } else if (banner.type === 'prompt' && banner.messageType === 'payment_past_due') {
    return <PaymentFailureBanner banner={banner} />
  } else if (banner.type === 'prompt' && banner.messageType === 'product_hunt_launch') {
    // No longer render this banner
  } else if (banner.type === 'prompt' && banner.messageType === 'product_hunt_golden_kitty') {
    // No longer render this banner
  } else if (
    banner.type === 'announcement' &&
    ['wefunder_exclusive', 'wefunder_round_2'].includes(banner.messageType)
  ) {
    return <WefunderExclusiveBanner banner={banner} />
  } else if (banner.type === 'announcement' && banner.messageType === 'wefunder_series_a') {
    return <WefunderSeriesABanner banner={banner} />
  } else if (banner.type === 'announcement' && banner.messageType === 'product_hunt_launch_2023') {
    return <ProductHuntLaunchBanner banner={banner} />
  } else if (banner.message) {
    if (banner.type === 'announcement') {
      return <AnnouncementBanner banner={banner} />
    } else if (banner.type === 'click_here') {
      return <ClickHereBanner banner={banner} />
    } else if (banner.type === 'schedule_call') {
      return <ScheduleCallBanner banner={banner} />
    } else {
      return (
        <div class='flex justify-center p-3 text-md text-center text-black bg-brand rounded-t-md'>
          <div>
            <span class='font-extrabold'>{banner.message[0]}</span>&nbsp;
            {banner.message.length > 1 && <span>{banner.message.slice(1).join(' ')}</span>}
          </div>
        </div>
      )
    }
  }
}

export const GameButton: FunctionalComponent<{ earnedPoints: number } & Parameters<typeof InertiaLink>[0]> = (
  { earnedPoints, class: className = '', ...attrs },
  _children
) => (
  <InertiaLink
    {...attrs}
    class={`${className} z-menu flex-shrink-0 relative group p-2 font-semibold leading-none flex items-center justify-center whitespace-no-wrap hover:bg-info-warn rounded-md hover:bg-opacity-10`}
    style={{ fontSize: '20px' }}
    onMouseEnter={showDialog}
    onMouseLeave={hideDialog}
  >
    <GameBadge showMayor={true} earnedPoints={earnedPoints} />

    {/* new points notification */}
    <ui-popper position='bottom' visible={!state.showDialog && state.showTooltip} margin={5}>
      <div class='py-1 px-7px text-xs text-surface-base uppercase bg-info-warn rounded'>
        <span class='font-extrabold'>+{state.tooltipPoints}</span>
        <span class='font-semibold'>&nbsp;{pluralize('point', state.tooltipPoints, false)}</span>
      </div>
      <ui-pointer fillColor='info-warn' strokeColor='info-warn' strokeWidth={1} width={16} height={8} />
    </ui-popper>

    {/* points dialog */}
    <ui-tooltip anchor='bottom' margin={10} bgColor='info-warn' borderColor='info-warn' name=''>
      <div class='flex items-center text-xs text-surface-base' style={{ width: '300px', height: '253px' }}>
        <div class='absolute top-0 left-0 -mt-2 w-full h-2 bg-white bg-opacity-0'></div>
        <ui-icon
          icon={state.isMuted ? 'volume-mute' : 'volume-up'}
          onClick={() => (state.isMuted = !state.isMuted)}
          class='absolute top-0 right-0 mt-2 mr-2 text-xl opacity-50 hover:opacity-100 transition duration-200 cursor-pointer'
        />
        <div class='flex flex-col items-center mx-2' style={{ marginTop: '18px', marginBottom: '16px' }}>
          <div
            class='flex justify-center items-center text-info-warn bg-surface-base rounded-full'
            style={{
              width: '42px',
              height: '42px',
              paddingBottom: '.1em',
            }}
          >
            <ui-icon icon='star-fill' style={{ fontSize: '24px' }}></ui-icon>
          </div>
          <div class='flex pt-2 text-xl font-extrabold'>You have {pluralize('point', state.totalPoints, true)}</div>
          <div class='flex text-sm font-semibold'>Points are how you earn clout &amp; prizes</div>
          <br />
          <div class='flex pt-2 text-md font-medium text-center text-surface-base text-opacity-50'>
            By earning {pluralize('point', state.monthPoints, true)} this month you have that many chances to win this
            month's $1,000 Amazon Gift Card raffle*.
          </div>
          <br />
          <div class='pt-2 text-xs font-medium text-center text-surface-base text-opacity-50'>
            No purchase necessary. Void where prohibited. <br />
            Ends 12/31/2024. See{' '}
            <a class='underline' href='https://help.fathom.video/en/articles/295872' target='_blank'>
              Full Terms &amp; Conditions
            </a>
            .
          </div>
        </div>
      </div>
    </ui-tooltip>
  </InertiaLink>
)

export const GameInlineIconText: FunctionalComponent<
  {
    action: ActionName
    flip?: boolean
    iconClass?: string
  } & JSXBase.IntrinsicElements['span']
> = ({ action, flip, iconClass, class: className }) => (
  <InlineIconText icon='coin-star-fill' class={className} iconClass={iconClass} flip={flip}>
    {state.actions[action] ? pluralize('point', state.actions[action].points, true) : '0 points'}
  </InlineIconText>
)

// adds a little tilt to the mayoral crown
// const tilt = css`
//   transform: rotate(10deg);
// `
export const GameBadge: FunctionalComponent<
  {
    flip?: boolean
    action?: ActionName
    showMayor?: boolean
    tipAnchor?: string
    showEarned?: boolean
    earnedPoints?: number
    bgColor?: ColorsType
    borderColor?: ColorsType
  } & JSXBase.IntrinsicElements['aside']
> = ({
  flip,
  action,
  showMayor = false,
  tipAnchor = 'top',
  showEarned = false,
  earnedPoints,
  bgColor = 'surface-base',
  borderColor,
  class: className,
  ...attrs
}) => (
  <div class='relative'>
    {showMayor && state.isMayor && (
      <span>
        <ui-icon
          icon='crown-fill'
          class='absolute -mt-12px text-info-warn'
          style={{
            width: '20px',
            top: '5%',
          }}
        >
          <hr
            class='absolute text-surface-low bg-surface-low transform translate-y-14px'
            style={{
              width: '20px',
            }}
          />
        </ui-icon>
      </span>
    )}
    <aside
      class={`
      ${className} text-info-warn flex items-center
      ${classes(flip, 'flex-row-reverse')}
    `}
      style={{ lineHeight: '0' }}
      {...attrs}
      hidden={!action ? false : !state.actions[action] || state.actions[action].earned}
    >
      <span
        class='flex justify-center items-center'
        style={{
          width: '1em',
          height: '1em',
          margin: flip ? '0 0 0 0.3ch' : '0 0.3ch 0 0',
        }}
      >
        {action && !showEarned && (
          <ui-tooltip on='hover' anchor={tipAnchor as any} bgColor='info-warn' borderColor='transparent'>
            <span class='font-sans font-medium text-black uppercase'>
              Earn{' '}
              <span class='font-bold'>
                {state.actions[action] ? pluralize('point', state.actions[action].points, true) : '0 points'}
              </span>
            </span>
          </ui-tooltip>
        )}
        <ui-icon
          class={`
            text-info-warn rounded-full cursor-pointer
            ${classes(borderColor, 'border-3')}
            ${colorClasses({ border: borderColor, bg: bgColor })}
          `}
          icon='coin-star-fill'
          style={{ fontSize: '1em' }}
        ></ui-icon>
      </span>
      {action && showEarned && (
        <div class='flex font-sans text-xs font-medium text-info-warn uppercase'>
          Earned &nbsp;
          <span class='font-bold'>
            {state.actions[action] ? pluralize('point', state.actions[action].points, true) : '0 points'}
          </span>
        </div>
      )}
      {!action && (
        <ui-flipper
          sound={state.isMuted ? null : 'ding'}
          class='font-mono font-extrabold '
          style={{ fontSize: '.92em' }}
          value={earnedPoints}
        />
      )}
    </aside>
  </div>
)
